import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'js-cookie';
import omit from 'lodash/omit';
import assign from 'lodash/assign';

import { equalPasswords } from 'utils/validation/formValidationRules';
import SignUp from '../components/SignUp';
import { SIGNUP_FORM, SIGNUP_REQUIRE_PHONE_COUNTRIES, MAX_LENGTH_COUNTRY_CODE } from '../constants';
import { FM_REFERRAL_COOKIE_NAME } from 'constants/index';
import { SEGMENT_ANONYMOUS_ID } from '../../SignIn/constants';
import { getHub } from 'components/App/selectors';
import { getCountryByIpRequest } from 'modules/geolocation/actions';
import getAdditionalData from '../helpers/getAdditionalData';
import { useQuery } from '../hooks/useQuery';
import { getParams } from 'selectors';
import { getIsPhoneVerified, getPhoneVerificationToken, getUserCountry } from '../selectors/index';
import { createPendingSelector } from 'modules/api/selectors';
import { appleAuthRequest, appleUserExist, googleAuthRequest, signupRequest } from '../actions';
import { getUserDocuments } from '../../../selectors';
import { getFirstAvailableHub, getHubByRoutingHub } from 'helpers/hubs';
import { isCyHub } from '../../../helpers/hubs';

const SignUpContainer = () => {
  const dispatch = useDispatch();
  const isSignupPending = useSelector(createPendingSelector(signupRequest));
  const params = useSelector(getParams);
  const userCountry = useSelector(getUserCountry);
  const isPhoneVerified = useSelector(getIsPhoneVerified);
  const verificationToken = useSelector(getPhoneVerificationToken);
  const query = useQuery();
  const isPhoneVerificationRequired = useMemo(() => {
    return (
      SIGNUP_REQUIRE_PHONE_COUNTRIES.includes(userCountry) ||
      query.get('phoneVerification') === 'true'
    );
  }, [userCountry, query]);
  const routingHub = useSelector(getHub);
  const documents = useSelector(getUserDocuments);
  const currentHub = getHubByRoutingHub(routingHub) || getFirstAvailableHub();
  const cyHub = isCyHub(currentHub.KEY);
  const confirmMessageKey = cyHub ? 'confirm18YearsEU' : 'confirm18Years';
  const documentPath = cyHub ? documents.privacy : documents.terms;

  useEffect(() => {
    if (params.referral) {
      Cookies.set(FM_REFERRAL_COOKIE_NAME, params.referral, { sameSite: 'none', secure: true });
    }
  }, [params.referral]);

  useEffect(() => {
    dispatch(getCountryByIpRequest());
  }, [dispatch]);

  const additionalData = useMemo(() => {
    return getAdditionalData(params, routingHub);
  }, [params, routingHub]);

  const handleSubmit = useCallback(
    (data) => {
      data = assign({}, data, additionalData);
      data = omit(data, ['confirmPassword', 'conditions', 'country', 'phoneCountry']);
      const segmentAnonymousId = Cookies.get(SEGMENT_ANONYMOUS_ID);

      if (segmentAnonymousId) {
        data.segmentAnonymousId = segmentAnonymousId;
      }

      data?.phoneNumber?.length <= MAX_LENGTH_COUNTRY_CODE && (data = omit(data, ['phoneNumber']));
      data.verificationToken = verificationToken;

      dispatch(signupRequest(data));
    },
    [dispatch, additionalData, verificationToken],
  );

  const validate = useCallback(
    (data) => {
      let results = {};

      if (!data.conditions) {
        results._error = { key: 'common:errors.form.needConfirmYouAreOver18' };
      }

      if (isPhoneVerificationRequired && !isPhoneVerified) {
        results._error = { key: 'common:errors.form.needVerifyPhone' };
      }
      results = assign(results, equalPasswords(data));
      return results;
    },
    [isPhoneVerified, isPhoneVerificationRequired],
  );

  const handleGoogleSignupSuccess = useCallback(
    (data) => {
      data = assign({}, data, additionalData);
      dispatch(googleAuthRequest(data));
    },
    [dispatch, additionalData],
  );

  const handleAppleSignupSuccess = useCallback(
    (data) => {
      if (!data.appleUser) {
        dispatch(appleUserExist());
      } else {
        data = assign({}, data, additionalData);
        dispatch(appleAuthRequest(data));
      }
    },
    [dispatch, additionalData],
  );

  return (
    <SignUp
      onSubmit={handleSubmit}
      form={SIGNUP_FORM}
      validate={validate}
      isPending={isSignupPending}
      onGoogleSignupSuccess={handleGoogleSignupSuccess}
      onAppleSignupSuccess={handleAppleSignupSuccess}
      phoneVerificationRequired={isPhoneVerificationRequired}
      documentPath={documentPath}
      confirmMessageKey={confirmMessageKey}
    />
  );
};

export default SignUpContainer;
